
import { Component, Vue } from "vue-property-decorator";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vTable from "@/components/vTable.vue";
import vModal from "@/components/vModal.vue";
import { mapState } from "vuex";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import ListRequest, { Ordering, Filter, OrderType } from "@/models/ListRequest";
import { getErrorMessage } from "@/utils/Helpers";
import { IngredientCost } from "@/models/IngredientCost/IngredientCost";
import { RecipeCost } from "@/models/RecipeCost/RecipeCost";
import RecipeCostRepository from "@/utils/API/RecipeCostRepository";
import { dateTimeDisplayFormat } from "@/utils/Defaults";

const vSelectFx = () => import("@/components/vSelectFx.vue");

@Component({
  components: {
    vBreadcrumbs,
    vModal,
    vSelectFx,
    vTable,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
class RecipeCostComponent extends Vue {
  localizationState: any;

  recipeCosts: RecipeCost[] = [];
  ingredientCosts: IngredientCost[] = [];
  newIngredientCosts: any[] = [];
  massMeasurements: any[] = [];
  vendors: any[] = [];

  totalCount: number = 0;
  pageSize: number = 10;
  count: number = 0;
  page: number = 1;
  filter: Filter = {
    name: "",
    value: "",
  };
  ordering: Ordering = {
    field: "",
    orderType: OrderType.Asc,
  };

  editingIngredientCost: any = {};
  addingVendorName: string = "";
  loading: boolean = false;
  searchString = "";

  deletingModal = {
    isOpen: false,
    id: 0,
  };

  changePage(args: any) {
    let page = args[0];
    let filters = args[2];
    let orderings = args[3];

    this.page = page;
    this.ordering =
      orderings.length > 0
        ? orderings[0]
        : { field: "", orderType: OrderType.Asc };
    this.filter = filters.length > 0 ? filters[0] : { name: "", value: "" };

    this.requestRecipeCost();
  }

  order(filters: Filter[], orders: Ordering) {
    this.ordering = orders;
    this.filter = filters.length > 0 ? filters[0] : { name: "", value: "" };

    this.requestRecipeCost();
  }

  // Request for ListRecipeCosts
  requestRecipeCost() {
    let orderings = [this.ordering];
    let filters = [this.filter];

    let reqData = new ListRequest({
      page: this.page,
      pageSize: this.pageSize,
      filters: filters,
      orders: orderings,
    });

    this.loading = true;

    RecipeCostRepository.listRecipeCosts(reqData)
      .then((response) => {
        this.count = response.data.count;
        this.totalCount = response.data.totalCount;
        this.recipeCosts = response.data.result;
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        this.$snotify.error(getErrorMessage(error, this.translations));
      });
  }

  // Rediret to Edit Recipe Cost Page
  goOnEditRecipeCostPage(recipeCostId: string) {
    this.$router.push({
      name: "updateRecipeCost",
      params: { id: recipeCostId },
    });
  }

  // On delete recipe cost button
  deleteRecipeCost(id: number) {
    let recCost = this.recipeCosts.find((x) => x.id === id);
    if (recCost != undefined) {
      this.deletingModal.isOpen = true;
      this.deletingModal.id = recCost.id;
    }
  }
  // Delete recipe cost
  submitDelete() {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("ingr-cost-loading"),
      () =>
        new Promise((success, reject) => {
          RecipeCostRepository.deleteRecipeCost(this.deletingModal.id)
            .then(() => {
              this.deletingModal.isOpen = false;
              this.requestRecipeCost();
              success({
                body: this.translations.get("ingr-cost-delete-success"),
              });
            })
            .catch((error) => {
              reject({ body: getErrorMessage(error, this.translations) });
            });
        }),
      {
        timeout: 3500,
        closeOnClick: true,
      }
    );
  }

  // Export Button
  get exportFile() {
    return `${process.env.VUE_APP_BASE_DOMAIN}/api/Processes/RecipeCosts/ExportRecipeCosts`;
  }

  get recipeCostsForGrid() {
    return this.recipeCosts.map((x) => {
      let notificationComponent: any = "";
      if (!x.isAnyIngredientCostUpdated && !x.isAnyIngredientCostDeleted && x.isAnySubRecipeCostUpdated) {
        notificationComponent = {
          component: "vImageWithTooltip",
          props: {
            imgSrc: require(`@/assets/Customer/assets/img/msg-update.png`),
            tooltipContent: this.translations.get("sub-recipe-cost-updated-msg"),
            tooltipPlacement: "bottom-start",
            imgStyle: "height: 25px; margin-left: 2px;",
          },
        };
      }
      else if (x.isAnyIngredientCostUpdated || x.isAnySubRecipeCostUpdated) {
        notificationComponent = {
          component: "vImageWithTooltip",
          props: {
            imgSrc: require(`@/assets/Customer/assets/img/msg-update.png`),
            tooltipContent: this.translations.get("rec-cost-updated-msg"),
            tooltipPlacement: "bottom-start",
            imgStyle: "height: 25px; margin-left: 2px;",
          },
        };
      }
      else if (x.isAnyIngredientCostDeleted) {
        notificationComponent = {
          component: "vImageWithTooltip",
          props: {
            imgSrc: require(`@/assets/Customer/assets/img/msg-delete.png`),
            tooltipContent: this.translations.get("rec-cost-deleted-msg"),
            tooltipPlacement: "bottom-start",
            imgStyle: "height: 22px; margin-left: 4px;",
          },
        };
      }

      return {
        meta: {
          id: x.id,
          link: x.recipe.name,
        },
        e1: (Math.round(x.totalCost * 100) / 100).toFixed(2),
        e2: this.$moment(x.createdOn).format(dateTimeDisplayFormat),
        e3: notificationComponent,
      };
    });
  }

  get tableHeaders() {
    return [
      {
        name: this.translations.get("rec-cost-table-name"),
        key: "RecipeName",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("rec-cost-table-total-cost"),
        key: "TotalRecipeCost",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("rec-cost-table-date"),
        key: "DateCreated",
        searchable: false,
        searchString: "",
        sortable: true,
      },
      {
        name: this.translations.get("rec-cost-table-notif"),
        key: "Notification",
        searchable: false,
        searchString: "",
        sortable: false,
      },
    ];
  }

  get hasRecipeCosts() {
    return this.recipeCosts.length > 0;
  }

  get translations() {
    return this.localizationState.translations;
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: [
          "common",
          "ingredients",
          "ingredient cost",
          "recipe costing",
        ],
      })
      .then(() => {
        document.title =
          this.translations.get("ingr-cost-title") +
          " " +
          this.translations.get("meta_title_postfix");
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else
          this.$snotify.error(this.translations.get("toastr_network_error"));
      });

    this.requestRecipeCost();
  }

  mounted() {
    (<any>window).$.Pages.init();

    (<any>$("#select2RecipeCost"))
      .select2({
        allowClear: false,
        width: "100%",
        ajax: {
          delay: 500,
          transport: (params: any, success: any) => {
            RecipeCostRepository.searchRecipesWithCosts(this.searchString)
              .then((response) => {
                success(response);
              })
              .catch((error) => {
                this.$snotify.error(getErrorMessage(error, this.translations));
              });
          },
          processResults: (response: any) => {
            let data: any[] = [];
            data.push({ id: 0, text: "All Recipe costs" });
            response.data.result.map((r: any) => {
              data.push({ id: r.id, text: r.name });
            });

            return { results: data };
          },
        },
      })
      .on("select2:select", (eventData: any) => {
        this.page = 1;
        this.ordering = { field: "", orderType: OrderType.Asc };

        if (eventData.target.value == 0) {
          this.filter = {
            name: "",
            value: "",
          };
        }
        else {
          this.filter = { name: "RecipeId", value: eventData.target.value };
        }

        this.requestRecipeCost();
      });
  }
}
export default RecipeCostComponent;
